import { ChevronRight, Phone, Mail } from 'lucide-react';
import {
  COMPANY_CIN,
  CURRENT_YEAR,
  CUSTOMER_CARE_MAILTO,
  CUSTOMER_CARE_TEL,
} from '@/shared/constants/contact';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shared/widgets/accordion';
import { getCdnBaseUrl } from '@/core/utils/apiUtils';
import FollowUs from '@/shared/components/FollowUs';
import { WeAccept } from '@/shared/widgets/we-accept';
import footerSectionsData from '../data/footerSections.json';

interface FooterItem {
  label: string;
  href: string;
}

interface FooterSection {
  title: string;
  items: FooterItem[];
  isLink?: boolean;
}

const footerSections: FooterSection[] = footerSectionsData as FooterSection[];

export default function FooterMobile() {
  return (
    <footer className="bg-[#001e38] text-white">
      <div className="container-fluid py-8">
        {/* App Download Section */}
        <div className="text-center mb-8">
          <h2 className="text-lg font-semibold mb-4">EXPERIENCE BLUESTONE APP</h2>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="https://play.google.com/store/apps/details?id=com.bluestone.android&referrer=website&utm_source%3Dwebsite_footer%26utm_medium%3Dwebsite%26utm_campaign%3Dwebsite"
              target="_blank"
              rel="nofollow noopener noreferrer"
              aria-label="Download on Google Play"
              className="bg-transparent hover:opacity-80 p-0 transition-opacity inline-flex"
            >
              <img src={`${getCdnBaseUrl()}assets/gplay.svg`} alt="Google Play" className="h-10 w-auto" />
            </a>
            <a
              href="https://itunes.apple.com/in/app/bluestone-jewellery-online/id1107631430?mt=8"
              target="_blank"
              rel="nofollow noopener noreferrer"
              aria-label="Download on the App Store"
              className="bg-transparent hover:opacity-80 p-0 transition-opacity inline-flex"
            >
              <img src={`${getCdnBaseUrl()}assets/appstore.svg`} alt="App Store" className="h-10 w-auto" />
            </a>
          </div>
        </div>

        {/* Expandable Sections */}
        <div className="mb-6">
          <Accordion type="multiple" className="space-y-2">
            {footerSections
              .filter((section) => !section.isLink)
              .map((section) => (
                <AccordionItem
                  key={section.title}
                  value={section.title}
                  className="border-b border-white/20"
                >
                  <AccordionTrigger className="text-sm font-medium text-white hover:text-gray-300 hover:no-underline py-4">
                    {section.title}
                  </AccordionTrigger>
                  <AccordionContent className="space-y-2">
                    {section.items.map((item, index) => (
                      <a
                        key={index}
                        href={item.href || '#'}
                        className="block py-2 text-sm text-white/80 hover:text-white transition-colors"
                      >
                        {item.label}
                      </a>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              ))}
          </Accordion>
          {/* FAQ Link */}
          {footerSections
            .filter((section) => section.isLink)
            .map((section) => {
              const item = section.items[0];
              return (
                <div key={section.title} className="border-b border-white/20">
                  <a
                    href={item?.href || '#'}
                    className="flex items-center justify-between py-4 text-sm font-medium hover:text-gray-300 transition-colors"
                  >
                    <span>{item?.label || section.title}</span>
                    <ChevronRight className="h-5 w-5" />
                  </a>
                </div>
              );
            })}
        </div>

        {/* Contact Buttons */}
        <div className="flex gap-3 mb-6">
          <a
            href={CUSTOMER_CARE_TEL}
            aria-label="Call customer care"
            className="flex flex-1 flex-col items-center justify-center gap-0.5 rounded-md border border-white bg-white px-2 py-2 text-xs font-medium text-[#001e38] transition-colors hover:bg-gray-100"
          >
            <Phone className="h-4 w-4 shrink-0" aria-hidden />
            <span>CALL US</span>
          </a>
          <a
            href={CUSTOMER_CARE_MAILTO}
            aria-label="Email customer care"
            className="flex flex-1 flex-col items-center justify-center gap-0.5 rounded-md border border-white bg-white px-2 py-2 text-xs font-medium text-[#001e38] transition-colors hover:bg-gray-100"
          >
            <Mail className="h-4 w-4 shrink-0" aria-hidden />
            <span>WRITE TO US</span>
          </a>
        </div>

        {/* Social Media Section */}
        <FollowUs variant="mobile" />

        {/* Payment Methods */}
        <WeAccept variant="mobile" />

        {/* Copyright */}
        <div className="text-center text-sm text-white/80 space-y-1">
          <p>© {CURRENT_YEAR} BlueStone. All Rights Reserved.</p>
          <p>CIN: {COMPANY_CIN}</p>
        </div>
      </div>
    </footer>
  );
}

